// Kehilanet auth API client for the /khilanet panel.
//
// Three-leg flow against the Kehilanet auth service (jwtasp/jwtasp/auth.asp — an ASP.NET/IIS
// service owned by another team):
//   1. login    — username           -> access_token (Role "2FA"), refresh_token, otp_type
//   2. otp       — 2FA token + otp    -> access_token (Role "consent"), refresh_token
//   3. userData  — consent token      -> { user_id, given_name, ..., phones, communityid, communityName }
//      refresh   — consent token + refresh_token -> fresh consent access_token
//
// TWO THINGS ABOUT THE SERVICE:
//   - The `action` query param is CASE-SENSITIVE. The OTP leg is `action=otp` (lowercase);
//     `action=OTP` silently returns an empty 200 and never mints the consent token. The exact
//     action strings the server expects are: 'login', 'otp', 'userData', 'refresh' — don't
//     "normalize" their casing.
//   - The service currently sends no CORS headers, replies in windows-1255, and its userData
//     body is quirky JSON (unescaped inner quotes; `phones`/`communityid`/`communityName` come
//     back as stringified pseudo-arrays like "[12917,13637]"). CORS is being added service-side
//     — until then a browser call fails the preflight. The encoding + loose-JSON quirks are
//     handled here: this client decodes the response per its declared charset and tolerates a
//     non-JSON userData body. See KEHILANET_AUTH.md.
//
// Dev fallback: in non-production builds only, username `0523971371` + OTP `000000` short-
// circuit the network and resolve to DUMMY_USER (see khilanetData.ts) so the panel works
// offline. In production the dummy is inert and only real Kehilanet credentials work.

import { DUMMY_USER, DUMMY_OTP, DUMMY_COMMUNITY } from '../khilanetData'

// Base URL of the Kehilanet auth service (the `?action=` query is appended per call). Override
// per-env with REACT_APP_KHILANET_AUTH_BASE — e.g. the production host, or a local proxy if you
// ever need one before the service has CORS. Default is the test environment.
const AUTH_BASE =
  process.env.REACT_APP_KHILANET_AUTH_BASE ||
  'https://www.kehilanet-test.co.il/jwtasp/jwtasp/auth.asp'

// Exact action strings the service expects (see header note about case sensitivity).
type KhilanetAction = 'login' | 'otp' | 'userData' | 'refresh'

const DUMMY_ENABLED = process.env.NODE_ENV !== 'production'
const DUMMY_2FA_TOKEN = 'dummy.2fa.token'
const DUMMY_CONSENT_TOKEN = 'dummy.consent.token'
const DUMMY_REFRESH_TOKEN = 'dummy.refresh.token'
// Matches the service's expires_in (30 days).
const DUMMY_EXPIRES_IN = 2592000

// ---- public result shapes (normalized — callers never see service quirks) --------------------

export interface KhilanetLoginResult {
  /** Short-lived "2FA" token; pass to verifyOtp. */
  twoFAToken: string
  refreshToken: string
  /** How the OTP was delivered, e.g. "sms". */
  otpType: string
  /** Seconds until twoFAToken expires. */
  expiresIn: number
}

export interface KhilanetOtpResult {
  /** "consent" token; use as the Bearer for userData/refresh and as the panel's jwtToken. */
  consentToken: string
  refreshToken: string
  expiresIn: number
  /** Epoch ms when consentToken expires; 0 if the service didn't report an expiry. */
  expiresAt: number
}

export interface KhilanetCommunity {
  id: number
  name: string
}

export interface KhilanetUserData {
  userId: number | null
  givenName: string
  familyName: string
  displayName: string
  nickName: string
  phones: string[]
  communities: KhilanetCommunity[]
}

// ---- errors ----------------------------------------------------------------------------------

export type KhilanetAuthErrorKind = 'not-registered' | 'wrong-otp' | 'unauthorized' | 'network' | 'unknown'

export class KhilanetAuthError extends Error {
  kind: KhilanetAuthErrorKind
  status?: number
  constructor(kind: KhilanetAuthErrorKind, message: string, status?: number) {
    super(message)
    this.name = 'KhilanetAuthError'
    this.kind = kind
    this.status = status
  }
}

// ---- low-level transport ---------------------------------------------------------------------

interface RawEnvelope {
  success?: boolean
  message?: string
  access_token?: string
  refresh_token?: string
  expires_in?: number
  otp_type?: string
  // userData leg:
  user_id?: number
  given_name?: string
  family_name?: string
  displayName?: string
  nick_name?: string
  phones?: string | string[]
  communityid?: string | number[]
  communityName?: string | string[]
}

async function call(action: KhilanetAction, body: unknown, bearer?: string): Promise<RawEnvelope> {
  const url = `${AUTH_BASE}?action=${action}`
  let res: Response
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
      },
      body: JSON.stringify(body ?? {}),
    })
  } catch (e) {
    // Includes the CORS-preflight failure until the service adds CORS headers.
    throw new KhilanetAuthError('network', `Failed to reach Kehilanet auth service: ${String(e)}`)
  }

  const text = await readBody(res)
  const data = parseLenient(text)

  if (!res.ok) {
    const msg = (data && typeof data.message === 'string' && data.message) || `HTTP ${res.status}`
    // The service uses 401 both for a wrong token type and for bad creds; callers refine.
    throw new KhilanetAuthError(res.status === 401 ? 'unauthorized' : 'unknown', msg, res.status)
  }
  return data ?? {}
}

// The service replies `Content-Type: application/json; Charset=windows-1255` (Hebrew), and
// `Response.text()` would decode it as UTF-8 and mangle the names. Read the bytes and decode by
// the declared charset; falls back to UTF-8 for anything unrecognized (incl. if the service
// later switches to UTF-8).
async function readBody(res: Response): Promise<string> {
  if (typeof TextDecoder === 'undefined') return res.text()
  const buf = await res.arrayBuffer()
  const charset = charsetOf(res.headers.get('Content-Type'))
  try {
    return new TextDecoder(charset).decode(buf)
  } catch {
    try {
      return new TextDecoder('utf-8').decode(buf)
    } catch {
      return ''
    }
  }
}

function charsetOf(contentType: string | null): string {
  const m = contentType && contentType.match(/charset=([^;]+)/i)
  return (m ? m[1] : 'utf-8').trim().toLowerCase()
}

// Tolerant parse: the service's userData body can contain unescaped inner quotes
// (`"displayName":"X "Y""`), which is not valid JSON. Scrape the fields we need out of the raw
// text instead of throwing.
function parseLenient(text: string): RawEnvelope | null {
  const trimmed = (text || '').trim()
  if (!trimmed) return null
  try {
    return JSON.parse(trimmed) as RawEnvelope
  } catch {
    return scrapeEnvelope(trimmed)
  }
}

function scrapeEnvelope(text: string): RawEnvelope {
  const str = (k: string): string | undefined => {
    const m = text.match(new RegExp(`"${k}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*?)"\\s*(?:,|})`))
    return m ? m[1].replace(/\\(.)/g, '$1') : undefined
  }
  const num = (k: string): number | undefined => {
    const m = text.match(new RegExp(`"${k}"\\s*:\\s*(-?\\d+)`))
    return m ? Number(m[1]) : undefined
  }
  return {
    success: /"success"\s*:\s*true/.test(text),
    message: str('message'),
    access_token: str('access_token'),
    refresh_token: str('refresh_token'),
    expires_in: num('expires_in'),
    otp_type: str('otp_type'),
    user_id: num('user_id'),
    given_name: str('given_name'),
    family_name: str('family_name'),
    displayName: str('displayName'),
    nick_name: str('nick_name'),
    phones: str('phones'),
    communityid: str('communityid'),
    communityName: str('communityName'),
  }
}

// `phones` / `communityid` / `communityName` arrive as strings shaped like "[a,b,c]" (not JSON
// arrays). Turn that into a real array of trimmed, non-empty strings. Already-array input passes
// through. NOTE: a value containing a literal comma would split wrongly — see KEHILANET_AUTH.md.
function toList(value: string | unknown[] | undefined): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean)
  if (typeof value !== 'string') return []
  const inner = value.trim().replace(/^\[/, '').replace(/\]$/, '')
  if (!inner) return []
  return inner.split(',').map((s) => s.trim()).filter(Boolean)
}

function normalizeUserData(raw: RawEnvelope): KhilanetUserData {
  const ids = toList(raw.communityid).map((s) => Number(s)).filter((n) => Number.isFinite(n))
  const names = toList(raw.communityName)
  const communities: KhilanetCommunity[] = ids.map((id, i) => ({ id, name: names[i] ?? String(id) }))
  return {
    userId: typeof raw.user_id === 'number' ? raw.user_id : null,
    givenName: raw.given_name ?? '',
    familyName: raw.family_name ?? '',
    displayName: raw.displayName ?? '',
    nickName: raw.nick_name ?? '',
    phones: toList(raw.phones),
    communities,
  }
}

// ---- dev-only dummy fallback -----------------------------------------------------------------

function isDummyUsername(username: string): boolean {
  if (!DUMMY_ENABLED) return false
  return username.replace(/\D/g, '') === DUMMY_USER.phones[0].replace(/\D/g, '')
}

function dummyUserData(): KhilanetUserData {
  return {
    userId: DUMMY_USER.user_id,
    givenName: DUMMY_USER.given_name,
    familyName: DUMMY_USER.family_name,
    displayName: DUMMY_USER.displayName,
    nickName: DUMMY_USER.nick_name,
    phones: [...DUMMY_USER.phones],
    communities: [{ id: DUMMY_COMMUNITY.id, name: DUMMY_COMMUNITY.name }],
  }
}

// ---- public API ------------------------------------------------------------------------------

/** Leg 1 — start the flow for a username (phone or email). Sends an OTP, returns a 2FA token. */
export async function khilanetLogin(username: string): Promise<KhilanetLoginResult> {
  const u = username.trim()
  if (isDummyUsername(u)) {
    return { twoFAToken: DUMMY_2FA_TOKEN, refreshToken: DUMMY_REFRESH_TOKEN, otpType: 'sms', expiresIn: DUMMY_EXPIRES_IN }
  }
  const data = await call('login', { username: u })
  if (data.success === false || !data.access_token) {
    throw new KhilanetAuthError('not-registered', data.message || 'Username not recognized')
  }
  return {
    twoFAToken: data.access_token,
    refreshToken: data.refresh_token ?? '',
    otpType: data.otp_type ?? 'sms',
    expiresIn: data.expires_in ?? 0,
  }
}

/** Leg 2 — verify the OTP against the 2FA token; returns the consent token on success. */
export async function khilanetVerifyOtp(twoFAToken: string, otp: string): Promise<KhilanetOtpResult> {
  const code = otp.trim()
  if (DUMMY_ENABLED && twoFAToken === DUMMY_2FA_TOKEN) {
    if (code !== DUMMY_OTP) throw new KhilanetAuthError('wrong-otp', 'Incorrect code')
    return {
      consentToken: DUMMY_CONSENT_TOKEN,
      refreshToken: DUMMY_REFRESH_TOKEN,
      expiresIn: DUMMY_EXPIRES_IN,
      expiresAt: Date.now() + DUMMY_EXPIRES_IN * 1000,
    }
  }
  const data = await call('otp', { otp: code }, twoFAToken)
  // Service quirk: a wrong/expired OTP comes back as success:false or an empty body (no token).
  if (data.success === false || !data.access_token) {
    throw new KhilanetAuthError('wrong-otp', data.message || 'Incorrect or expired code')
  }
  const expiresIn = data.expires_in ?? 0
  return {
    consentToken: data.access_token,
    refreshToken: data.refresh_token ?? '',
    expiresIn,
    expiresAt: expiresIn ? Date.now() + expiresIn * 1000 : 0,
  }
}

/** Leg 3 — fetch the signed-in user's profile + communities using the consent token. */
export async function khilanetGetUserData(consentToken: string): Promise<KhilanetUserData> {
  if (DUMMY_ENABLED && consentToken === DUMMY_CONSENT_TOKEN) return dummyUserData()
  const data = await call('userData', {}, consentToken)
  if (data.success === false) throw new KhilanetAuthError('unauthorized', data.message || 'Could not load user data')
  return normalizeUserData(data)
}

/** Exchange a consent refresh_token for a fresh consent access_token. */
export async function khilanetRefresh(consentToken: string, refreshToken: string): Promise<KhilanetOtpResult> {
  if (DUMMY_ENABLED && consentToken === DUMMY_CONSENT_TOKEN) {
    return {
      consentToken: DUMMY_CONSENT_TOKEN,
      refreshToken: DUMMY_REFRESH_TOKEN,
      expiresIn: DUMMY_EXPIRES_IN,
      expiresAt: Date.now() + DUMMY_EXPIRES_IN * 1000,
    }
  }
  const data = await call('refresh', { refresh_token: refreshToken }, consentToken)
  if (data.success === false || !data.access_token) {
    throw new KhilanetAuthError('unauthorized', data.message || 'Could not refresh session')
  }
  const expiresIn = data.expires_in ?? 0
  return {
    consentToken: data.access_token,
    refreshToken: data.refresh_token ?? refreshToken,
    expiresIn,
    expiresAt: expiresIn ? Date.now() + expiresIn * 1000 : 0,
  }
}
